
import Vue from "vue";
import TitledSection from "@/shared/components/TitledSection.vue";
import TitledSubsection from "@/shared/components/TitledSubsection.vue";
import EventsTable from "@/shared/components/EventsTable.vue";
import CommentSection from "@/features/Comments/CommentSection.vue";
import LabeledItem from "@/shared/components/LabeledItem.vue";
import Scorecard from "@/features/Scores/Scorecard";
import Section from "@/features/Scores/Section";
import Score, { ScoreStatus, ScoreValueType, UIScoreStatus, QuestionAnswerOption } from "@/features/Scores/Score";
import Rating from "@/features/Scores/Rating";
import { CommentSummary, CommentType } from "@/features/Comments/Comment";
import { formatDateWithTimestamp, formatDatepickerDate, parseDateAsUTC } from "@/shared/utils/common_utils";
import AppError from "@/shared/components/AppError.vue";

export default Vue.extend({
    name: "ScorecardDetails",
    components: {
        TitledSection,
        TitledSubsection,
        EventsTable,
        CommentSection,
        LabeledItem,
        AppError,
    },
    data: () => ({
        uiScoreStatus: UIScoreStatus,
        scoreValueType: ScoreValueType,
        scorecard: null! as Scorecard,
        scorecardValid: false,
        dateMenu: false,
        commentType: CommentType.ScoreComment,
        commentSummaries: [] as CommentSummary[],
        loading: false,
        // this object helps the data table keep track of which questions are expanded at any given time
        expandeds: {} as { [key: string]: [] },
        errorMessage: "",
        showError: false,
        editMode: false,
        showConfirmationDialog: false,
        dataIsNewScorecard: false,
        lastRatings: [] as Rating[],
    }),
    props: {
        scorecardID: {
            type: String,
            required: true,
        },
        supplierID: {
            type: String,
            required: true,
        },
        isNewScorecard: {
            type: Boolean,
            default: false,
        },
    },
    async mounted() {
        // Fetch scorecard
        await this.getScorecard(this.scorecardID);

        this.dataIsNewScorecard = this.isNewScorecard;
        this.editMode = this.dataIsNewScorecard;
    },
    watch: {
        scorecardID(newValue: string | null) {
            if (newValue) {
                this.getScorecard(newValue);
            }
        },
        assessmentDate() {
            this.getLastRatings();
        },
    },
    methods: {
        getScorecard: async function (id: string) {
            this.loading = true;
            try {
                const scorecardTask = this.$scoreService.getScorecardFromServer(id);
                const commentTask = this.$commentService.getCommentSummaries(CommentType.ScoreComment, id);

                this.scorecard = await scorecardTask;
                this.commentSummaries = await commentTask;
                // Sort sections and questions for display
                this.scorecard?.sections.sort((s1, s2) => s1.order - s2.order);
                this.scorecard?.sections.forEach(section => section.scores.sort((s1, s2) => s1.order - s2.order));
            } catch {
                this.errorMessage = "Failed to fetch scorecard";
                this.showError = true;
            } finally {
                this.loading = false;
            }
        },
        async getLastRatings(): Promise<void> {
            try {
                this.lastRatings = await this.$scoreService.getLastRatings(this.supplierID, this.scorecard.assessmentDate);
            } catch {
                this.errorMessage = "Failed to fetch latest ratings";
                this.showError = true;
            }
        },
        allScoresAnswered() {
            return this.scorecard.sections.every(s => s.scores.every(sc => this.hasBeenAnswered(sc)));
        },
        hasBeenAnswered(score: Score): boolean {
            return score.isSkipped || this.getNumericalValueForScore(score) != null;
        },
        onScorecardChanged() {
            this.$emit("update:scorecard", this.scorecard);
        },
        formatDateWithTimestampForDisplay(dateString: string) {
            const date = parseDateAsUTC(dateString);
            return formatDateWithTimestamp(date);
        },
        formatDatepickerDateForDisplay(dateString: string) {
            const date = parseDateAsUTC(dateString);
            return formatDatepickerDate(date);
        },
        commentAdded: async function () {
            try {
                this.commentSummaries = await this.$commentService.getCommentSummaries(CommentType.ScoreComment, this.scorecard.id);
            } catch {
                this.errorMessage = "Failed to fetch updated comment data";
                this.showError = true;
            }
        },
        maxRatingForScore(score: Score): number {
            // Get the max rating for the score, depending on its value type:
            // for min/max scores, get the maximum possible answer,
            // for option select scores, get the possible answer with the greastest value
            return score.valueType == ScoreValueType.minMax ? score.answerMax! : score.possibleAnswers!.reduce((c, d) => (d.value > c.value ? d : c)).value;
        },
        reduceTotalPossibleSectionScore(sum: number, score: Score): number {
            const maxRating = this.maxRatingForScore(score);

            // Skipped scores don't contribute to the total possible value for that section
            return sum + (score.isSkipped ? 0 : maxRating * score.weight);
        },
        getSectionScore(section: Section): number {
            return section.scores.reduce((a, b) => {
                const scoreValue = this.getNumericalValueForScore(b) ?? 0;
                return a + (b.isSkipped ? 0 : scoreValue * b.weight);
            }, 0);
        },
        prepareScoresForTable(scores: Score[]) {
            return scores.map(score => {
                const summary = this.commentSummaries.length ? this.commentSummaries.find(s => score.id == s.parentID) : null;
                return {
                    id: score.id,
                    commentType: CommentType.ScoreComment,
                    title: score.title,
                    description: score.description,
                    value: !score.isSkipped ? this.getValueStringForScore(score) : "",
                    weight: score.weight,
                    score: (!score.isSkipped ? this.getNumericalValueForScore(score) : 0)! * score.weight,
                    possibleScore: score.weight * this.maxRatingForScore(score),
                    isSkipped: score.isSkipped,
                    canBeSkipped: score.canBeSkipped,
                    comments: summary ? summary.commentCount : "",
                    attachments: summary ? summary.attachmentCount : "",
                    events: score.events,
                    valueType: score.valueType,
                    possibleAnswers: score.possibleAnswers,
                    answerMin: score.answerMin,
                    answerMax: score.answerMax,
                    lastRating: this.lastRatings.find(r => r.questionSeriesID == score.questionSeriesID)?.value ?? "none",
                };
            });
        },
        getNumericalValueForScore(score: Score): number | null {
            if (score.valueType == ScoreValueType.minMax) {
                return score.minMaxValue ?? null;
            }
            if (score.valueType == ScoreValueType.optionSelect) {
                const answer = score.possibleAnswers?.find(a => a.answer == score.optionSelectValue);
                return answer ? answer.value : null;
            }
            return null;
        },
        getValueStringForScore(score: Score): string | QuestionAnswerOption {
            if (this.editMode) {
                // In edit mode, for OptionSelect scores we want to hand the v-select the possibleAnswer that has been chosen. The input then handles formatting that object for display.
                return score.valueType == ScoreValueType.minMax ? score.minMaxValue?.toString() ?? "" : score.possibleAnswers?.find(a => a.answer == score.optionSelectValue) ?? "";
            }

            if (!this.hasBeenAnswered(score)) {
                return "";
            }

            return score.valueType == ScoreValueType.minMax ? score.minMaxValue?.toString() ?? "" : `${score.optionSelectValue} (${this.getNumericalValueForScore(score)})`;
        },
        updateIsSkipped(sectionID: string, scoreID: string, newValue: boolean): void {
            // Find objects to update
            const section = this.scorecard.sections.find(s => s.id == sectionID)!;
            const score = section.scores.find(s => s.id == scoreID)!;

            if (score.isLocked) return;
            score.isSkipped = newValue;

            // update overall score for display
            this.scorecard.percentageScoreValue = this.calculateOverallPercentScore;
        },
        updateValue(sectionID: string, scoreID: string, newValue: number | QuestionAnswerOption): void {
            // Find objects to update
            const section = this.scorecard.sections.find(s => s.id == sectionID)!;
            const score = section.scores.find(s => s.id == scoreID)!;

            if (score.isLocked) return;
            if (!score.isSkipped) {
                // Update score's value by type
                if (score.valueType == ScoreValueType.minMax && newValue <= score.answerMax! && newValue >= score.answerMin!) {
                    score.minMaxValue = newValue as number;
                } else if (score.valueType == ScoreValueType.optionSelect && score.possibleAnswers!.some(a => a.answer == (newValue as QuestionAnswerOption).answer)) {
                    score.optionSelectValue = (newValue as QuestionAnswerOption).answer;
                }
            }

            // update overall score for display
            this.scorecard.percentageScoreValue = this.calculateOverallPercentScore;
        },
        saveScorecard: async function () {
            if (this.scorecardValid) {
                // Set the assessment date on every score
                this.scorecard.sections.forEach(section => section.scores.forEach(score => (score.assessmentDate = this.scorecard.assessmentDate)));

                try {
                    if (this.scorecard) {
                        await this.$scoreService.updateScorecard(this.scorecard!);
                        this.editMode = false;
                        this.dataIsNewScorecard = false;

                        // re-fetch scorecard for new ChangeTrackedEvents
                        this.$emit("scorecard-updated");
                        await this.getScorecard(this.scorecardID);
                    }
                } catch {
                    this.errorMessage = "Failed to save scorecard:";
                    this.showError = true;
                }
            }
        },
        cancelScorecard: async function () {
            // If it's a new scorecard, delete the one we just made.
            if (this.dataIsNewScorecard) {
                console.trace("Deleting new scorecard");
                try {
                    if (this.scorecard) {
                        await this.$scoreService.deleteScorecard(this.scorecard!.id);
                        this.$emit("scorecard-updated");
                        this.$emit("close-pane");
                    }
                } catch {
                    this.errorMessage = "Failed to delete scorecard";
                    this.showError = true;
                }
            } else {
                this.$emit("close-pane");
            }
        },
        publishScorecard: async function () {
            console.trace("Publishing scorecard");
            try {
                if (this.scorecard) {
                    await this.$scoreService.publishScorecard(this.scorecard.id);
                    this.editMode = false;

                    // update timeline and re-fetch scorecard for new ChangeTrackedEvents
                    this.$emit("scorecard-updated");
                    this.getScorecard(this.scorecardID);
                }
            } catch {
                this.errorMessage = "Failed to publish scorecard";
                this.showError = true;
            }
        },
        openConfirmation: async function () {
            this.showConfirmationDialog = true;
        },
        lockScorecard: async function () {
            console.trace("Locking scorecard");
            try {
                if (this.scorecard) {
                    await this.$scoreService.lockScorecard(this.scorecard.id);
                    this.editMode = false;
                    await this.getScorecard(this.scorecardID);
                }
            } catch {
                this.errorMessage = "Failed to lock scorecard";
                this.showError = true;
            }
        },
        editScorecard() {
            this.editMode = true;
        },
    },
    computed: {
        assessmentDate(): Date | null {
            return this.scorecard?.assessmentDate;
        },
        statusString(): string {
            let status = UIScoreStatus[this.scorecard.status];
            if (this.scorecard.isLocked) {
                status += " - Locked";
            }
            return status;
        },
        canPublishScorecard(): boolean {
            if (!this.supplierID || !this.scorecard || this.scorecard.isLocked || this.scorecard.status == ScoreStatus.published) {
                return false;
            }

            return this.$scoreAuthorizationService.canPublishScorecard(this.supplierID);
        },
        canLockScorecard(): boolean {
            if (!this.supplierID || !this.scorecard || this.scorecard.isLocked || this.scorecard.status == ScoreStatus.draft) {
                return false;
            }
            return this.$scoreAuthorizationService.canLockScorecard(this.supplierID);
        },
        calculateOverallRating(): number {
            // sum each section's current score
            return this.scorecard.sections.reduce((a, b) => a + this.getSectionScore(b), 0);
        },
        calculateOverallPossibleScore(): number {
            // map each section to its possible score, then sum them to get the total possible score for the scorecard
            return this.scorecard.sections.map(s => s.scores.filter(score => this.hasBeenAnswered(score)).reduce(this.reduceTotalPossibleSectionScore, 0)).reduce((a, b) => a + b, 0);
        },
        calculateOverallPercentScore(): number {
            return this.calculateOverallRating / this.calculateOverallPossibleScore;
        },
        tableHeaders() {
            const headers = [
                {
                    text: "Criteria",
                    align: "start",
                    sortable: false,
                    value: "title",
                    width: "50%",
                },
                {
                    text: "Rating",
                    align: "center",
                    sortable: false,
                    value: "value",
                    width: "10%",
                },
                {
                    text: "Weight",
                    align: "center",
                    sortable: false,
                    value: "weight",
                    width: "7%",
                },
                {
                    text: "Score",
                    align: "center",
                    sortable: false,
                    value: "score",
                    width: "14%",
                },
                {
                    text: "Skipped",
                    align: "center",
                    sortable: false,
                    value: "isSkipped",
                    width: "5%",
                },
                {
                    text: "Comments",
                    align: "center",
                    sortable: false,
                    value: "comments",
                    width: "7%",
                },
                {
                    text: "Attachments",
                    align: "center",
                    sortable: false,
                    value: "attachments",
                    width: "7%",
                },
            ];

            if (this.editMode) {
                // Insert the Last Rating column between the Criteria column and the Rating column
                headers.splice(1, 0, {
                    text: "Last Rating",
                    align: "center",
                    sortable: false,
                    value: "lastRating",
                    width: "7%",
                });
            }

            return headers;
        },
    },
});
